import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import './InstallerPanelTable.css';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import React, { useEffect, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { connect } from 'react-redux';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { getDeviceDetails } from '../../stores/actions/installerActions';
import { formatBatteries } from './DevicesList';
import MapLoading from '../Dashboard/MapLoading';

type MainSensorType = 'laser' | 'inclinometer' | 'none';

interface DeviceDetailsData {
  id: number | string;
  w2Id?: number;
  systemVersion: string;
  systemName: string;
  w2ParentId?: number;
  w2OperationOrder?: number;
  w2Operational?: boolean;
  active?: boolean;
  nodeName: string;
  deviceRole: string;
  deviceType: string;
  mainSensorType: MainSensorType;
  serialString?: string;
  subSerialString?: string;
  firmwareVersion?: string;
  mapPositionX?: number;
  mapPositionY?: number;
  permissibleValue?: number;
  thresholdLevel1?: number;
  thresholdLevel2?: number;
  thresholdLevel3?: number;
  thresholdLevel4?: number;
  dependenciesFormula?: string;
  inclinometerInverted?: boolean;
  w2ConnectionStatus?: number;
  rangefinderStatus?: number;
  supplyState?: string;
  batteryPercentage?: number;
  en91Count?: number;
  en92Count?: number;
  en95Count?: number;
  temperature1?: number;
  temperature2?: number;
  temperatureOk?: boolean;
  angleChangeThreshold?: number;
  distanceChangeThreshold?: number;
  measurementBaseAngle?: number;
  measurementBaseDistance?: number;
  baseAngleCorrection?: number;
  baseDistanceCorrection?: number;
  threatDuration?: number;
  obstructionStatus?: number;
}

interface DeviceDetailsProps {
  deviceDetails: DeviceDetailsData | null;
  mobileView: boolean;
  getDeviceDetails: (deviceId: string) => void;
}

interface Row {
  label: string;
  value: React.ReactNode;
}

const renderYesNo = (value: boolean | null | undefined) => {
  if (value === null || value === undefined) return null;
  return value ? 'Yes' : 'No';
};

const buildTitle = (deviceDetails: DeviceDetailsData): string => {
  const { nodeName, systemName, w2Id, id } = deviceDetails;

  if (!nodeName) {
    return String(id);
  }

  const w2Part = w2Id != null ? ` (${w2Id})` : '';
  const systemPart = systemName ? ` in ${systemName}` : '';
  return `${nodeName}${w2Part}${systemPart}`;
};

const DeviceDetails = ({ mobileView, deviceDetails, getDeviceDetails }: DeviceDetailsProps) => {
  const { deviceID } = useParams<{ deviceID: string }>();
  const history = useHistory();
  const isCurrentDeviceLoaded = deviceDetails != null && deviceID != null && Number(deviceDetails.id) === Number(deviceID);

  useEffect(() => {
    if (deviceID) {
      getDeviceDetails(deviceID);
    }
  }, [deviceID, getDeviceDetails]);

  const handleBack = () => {
    if (history.length > 2) {
      history.goBack();
    } else {
      history.push('/devices');
    }
  };

  const rows: Row[] = useMemo(() => {
    if (!deviceDetails) return [];

    const isW2 = deviceDetails.systemVersion === 'W2';
    const sensorType = deviceDetails.mainSensorType;
    const hasSensor = sensorType !== 'none';
    const showAngleFields = sensorType === 'inclinometer';
    const showDistanceFields = sensorType === 'laser';
    const showInclinometerInverted = sensorType === 'inclinometer';
    const unit = sensorType === 'inclinometer' ? '°' : 'mm';

    return [
      { label: 'Node', value: deviceDetails.nodeName },
      { label: 'System', value: deviceDetails.systemName },
      { label: 'System version', value: deviceDetails.systemVersion === 'W2' ? 'WSN' : 'LoRa' },
      ...(isW2 ? [{ label: 'W2 ID', value: deviceDetails.w2Id ?? null }] : []),
      ...(isW2 ? [{ label: 'W2 parent ID', value: deviceDetails.w2ParentId ?? null }] : []),
      ...(isW2 ? [{ label: 'W2 operation order', value: deviceDetails.w2OperationOrder ?? null }] : []),
      ...(isW2 ? [{ label: 'W2 operational', value: renderYesNo(deviceDetails.w2Operational) }] : []),
      ...(isW2 ? [{ label: 'W2 connection status', value: deviceDetails.w2ConnectionStatus ?? null }] : []),
      { label: 'Active', value: renderYesNo(deviceDetails.active) },
      { label: 'Device role', value: deviceDetails.deviceRole },
      { label: 'Device type', value: deviceDetails.deviceType },
      { label: 'Serial number', value: deviceDetails.serialString ?? null },
      { label: 'Sub serial number', value: deviceDetails.subSerialString ?? null },
      { label: 'Firmware version', value: deviceDetails.firmwareVersion ?? null },
      { label: 'Map position X', value: deviceDetails.mapPositionX ?? null },
      { label: 'Map position Y', value: deviceDetails.mapPositionY ?? null },
      ...(hasSensor
        ? [{ label: 'Permissible value', value: deviceDetails.permissibleValue != null ? `${deviceDetails.permissibleValue}${unit}` : null }]
        : []),
      ...(hasSensor ? [{ label: 'Threshold level 1', value: deviceDetails.thresholdLevel1 ? deviceDetails.thresholdLevel1 + '%' : null }] : []),
      ...(hasSensor ? [{ label: 'Threshold level 2', value: deviceDetails.thresholdLevel2 ? deviceDetails.thresholdLevel2 + '%' : null }] : []),
      ...(hasSensor ? [{ label: 'Threshold level 3', value: deviceDetails.thresholdLevel3 ? deviceDetails.thresholdLevel3 + '%' : null }] : []),
      ...(hasSensor ? [{ label: 'Threshold level 4', value: deviceDetails.thresholdLevel4 ? deviceDetails.thresholdLevel4 + '%' : null }] : []),
      ...(hasSensor ? [{ label: 'Dependencies formula', value: deviceDetails.dependenciesFormula ?? null }] : []),
      ...(hasSensor && showInclinometerInverted
        ? [{ label: 'Inclinometer inverted', value: renderYesNo(deviceDetails.inclinometerInverted) }]
        : []),
      ...(hasSensor ? [{ label: 'Sensor status', value: deviceDetails.rangefinderStatus ?? null }] : []),
      { label: 'Supply state', value: deviceDetails.supplyState ?? null },
      {
        label: 'Batteries',
        value: formatBatteries(deviceDetails.en91Count ?? 0, deviceDetails.en92Count ?? 0, deviceDetails.en95Count ?? 0),
      },
      { label: 'Battery level', value: deviceDetails.batteryPercentage != null ? `${deviceDetails.batteryPercentage}%` : null },
      {
        label: 'Temperature',
        value: deviceDetails.temperature1 != null
          ? (
            <span style={{ backgroundColor: deviceDetails.temperatureOk === false ? '#fddede' : 'transparent', padding: '2px 4px' }}>
              {`${deviceDetails.temperature1}°C`}
            </span>
          )
          : null,
      },
      { label: 'Sensor Temperature', value: deviceDetails.temperature2 != null ? `${deviceDetails.temperature2}°C` : null },
      { label: 'Temperature OK', value: renderYesNo(deviceDetails.temperatureOk) },
      ...(hasSensor && showAngleFields
        ? [{ label: 'Angle change threshold', value: deviceDetails.angleChangeThreshold != null ? `${deviceDetails.angleChangeThreshold}${unit}` : null }]
        : []),
      ...(hasSensor && showDistanceFields
        ? [{ label: 'Distance change threshold', value: deviceDetails.distanceChangeThreshold != null ? `${deviceDetails.distanceChangeThreshold}${unit}` : null }]
        : []),
      ...(hasSensor && showAngleFields
        ? [{ label: 'Measurement base angle', value: deviceDetails.measurementBaseAngle != null ? `${deviceDetails.measurementBaseAngle}${unit}` : null }]
        : []),
      ...(hasSensor && showDistanceFields
        ? [{ label: 'Measurement base distance', value: deviceDetails.measurementBaseDistance != null ? `${deviceDetails.measurementBaseDistance}${unit}` : null }]
        : []),
      ...(hasSensor && showAngleFields
        ? [{ label: 'Base angle correction', value: deviceDetails.baseAngleCorrection != null ? `${deviceDetails.baseAngleCorrection}${unit}` : null }]
        : []),
      ...(hasSensor && showDistanceFields
        ? [{ label: 'Base distance correction', value: deviceDetails.baseDistanceCorrection != null ? `${deviceDetails.baseDistanceCorrection}${unit}` : null }]
        : []),
      ...(hasSensor ? [{ label: 'Threat duration', value: deviceDetails.threatDuration ?? null }] : []),
      ...(hasSensor && showDistanceFields ? [{ label: 'Obstruction status', value: deviceDetails.obstructionStatus ?? null }] : []),
    ];
  }, [deviceDetails]);

  const title = deviceDetails ? buildTitle(deviceDetails) : '';

  if (!isCurrentDeviceLoaded) {
    return <MapLoading testID="device-details-loading" />;
  }

  return (
    <div>
      {deviceDetails && (
        <>
          <Tooltip target=".device-details-title" />
          <div
            className={mobileView ? 'flex flex-column gap-2 mb-3' : 'flex align-items-center gap-3 mb-3'}
          >
            <Button
              icon="pi pi-arrow-left"
              className="p-button-rounded p-button-outlined align-self-start"
              onClick={handleBack}
              aria-label="Back to devices list"
            />
            <h2
              className={`device-details-title m-0 ${mobileView ? 'text-xl' : ''}`}
              style={{
                fontWeight: 700,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: mobileView ? 'normal' : 'nowrap',
                wordBreak: mobileView ? 'break-word' : 'normal',
              }}
              data-pr-tooltip={mobileView ? undefined : title}
            >
              {title}
            </h2>
          </div>
        </>
      )}

      <DataTable
        value={rows}
        className="p-datatable-sm"
        showGridlines
        showHeaders={false}
        stripedRows
      >
        <Column
          field="label"
          body={(row: Row) => <span style={{ fontWeight: 600 }}>{row.label}</span>}
          style={{ width: mobileView ? '45%' : '30%' }}
        />
        <Column field="value" body={(row: Row) => row.value} />
      </DataTable>
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    deviceDetails: state.installer.deviceDetails,
    mobileView: state.dashboard.mobileView,
  };
};

const actions = {
  getDeviceDetails,
};

export default connect(mapStateToProps, actions)(DeviceDetails);